/**
 * @license
 * @copyright Copyright Motili Inc., 2025 All Rights Reserved
 */

/* eslint-disable react/jsx-props-no-spreading */

import { useState } from 'react';
import {
    Autocomplete,
    TextField,
    InputAdornment,
    ListSubheader,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { DisplayType } from 'common/types';
import * as React from 'react';

interface TypeAheadSearchBarProps<T> {
    options: T[];
    onChange: (value: T) => void;
    getOptionLabel?: (option: T) => string;
    isOptionEqualToValue?: (option: T, value: T) => boolean;
    disabled?: boolean;
    noOptionsText?: string;
    groupByKey?: string;
}

export default function TypeAheadSearchBar<T extends DisplayType>(
    props: TypeAheadSearchBarProps<T>
) {
    const {
        options = [],
        onChange,
        getOptionLabel = option => option.display,
        isOptionEqualToValue = (option, value) => option.id === value.id,
        disabled,
        noOptionsText = '',
        groupByKey,
    } = props;
    const [typedText, setTypedText] = useState('');
    return (
        <Autocomplete
            options={options}
            groupBy={
                groupByKey
                    ? option => option[groupByKey] || 'Uncategorized'
                    : undefined
            }
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={isOptionEqualToValue}
            disabled={disabled}
            value={null}
            onChange={(_, value) => {
                if (value) {
                    onChange(value);
                    setTypedText('');
                }
            }}
            inputValue={typedText}
            onInputChange={(_, newInputValue) => {
                setTypedText(newInputValue);
            }}
            blurOnSelect
            clearOnBlur
            forcePopupIcon={false}
            noOptionsText={noOptionsText}
            fullWidth
            slotProps={{
                paper: {
                    sx: {
                        fontFamily: 'Nunito Sans, sans-serif',
                        fontSize: 14,
                        fontWeight: 400,
                        margin: '4px 0',
                        borderRadius: 0,
                        boxShadow: '0px 2px 6px 0px #00000040',
                        border: '1px solid #ccc',
                    },
                },
                listbox: {
                    sx: {
                        padding: '12px 0',
                        '& .MuiAutocomplete-option': {
                            padding: '6px 12px',
                            lineHeight: '20px',
                        },
                    },
                },
            }}
            renderInput={params => (
                <TextField
                    {...params}
                    placeholder='Search...'
                    slotProps={{
                        input: {
                            ...params.InputProps,
                            startAdornment: (
                                <InputAdornment
                                    position='start'
                                    sx={{
                                        margin: 0,
                                        color: '#999999',
                                        '.Mui-focused &': {
                                            color: '#57C0E8',
                                        },
                                    }}
                                >
                                    <SearchIcon sx={{ fontSize: 20 }} />
                                </InputAdornment>
                            ),
                            sx: {
                                fontFamily: 'Nunito Sans, sans-serif',
                                fontSize: 14,
                                fontWeight: 400,
                                height: 40,
                                padding: '12px 0',
                                borderRadius: 0,
                                '& fieldset': { borderColor: '#ccc' },
                                '&:hover fieldset': {
                                    borderColor: '#ccc !important',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: '#57C0E8 !important',
                                    borderWidth: '1px !important',
                                },
                                '& input::placeholder': {
                                    color: '#818181',
                                    opacity: 1,
                                },
                                '&.Mui-disabled': {
                                    backgroundColor: '#eeeeee',
                                    color: 'rgba(0, 0, 0, 0.38)',
                                },
                            },
                        },
                    }}
                />
            )}
            renderGroup={
                groupByKey
                    ? params => (
                          <li key={params.key}>
                              <ListSubheader
                                  sx={{
                                      fontWeight: 'bold',
                                  }}
                              >
                                  {params.group}
                              </ListSubheader>
                              <ul style={{ padding: 0 }}>{params.children}</ul>
                          </li>
                      )
                    : undefined
            }
        />
    );
}
